import React, { useState } from 'react';

interface Step2LocationProps {
  country: string;
  city: string;
  workEligibility: boolean | null;
  onUpdate: (country: string, city: string, workEligibility: boolean | null) => void;
  onNext: () => void;
  onBack: () => void;
}

const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina',
  'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados',
  'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana',
  'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia', 'Cameroon',
  'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo',
  'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica',
  'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia',
  'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany',
  'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras',
  'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Jamaica',
  'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kosovo', 'Kuwait', 'Kyrgyzstan', 'Laos',
  'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
  'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania',
  'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco',
  'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua',
  'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau',
  'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
  'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia',
  'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia',
  'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia',
  'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka',
  'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania',
  'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey',
  'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom',
  'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
  'Yemen', 'Zambia', 'Zimbabwe'
];

export const Step2Location: React.FC<Step2LocationProps> = ({
  country,
  city,
  workEligibility,
  onUpdate,
  onNext,
  onBack,
}) => {
  const [error, setError] = useState('');

  const handleNext = () => {
    if (!country.trim()) {
      setError('Country is required');
      return;
    }

    if (!city.trim()) {
      setError('City is required');
      return;
    }

    if (workEligibility === null) {
      setError('Please indicate your work eligibility');
      return;
    }

    setError('');
    onNext();
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2 md:mb-3">Where are you currently located?</h1>
      <p className="text-xs md:text-base text-gray-600 mb-4 md:mb-8">Select your current location</p>

      <div className="space-y-4 md:space-y-6">
        <div>
          <label htmlFor="country" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
            Country <span className="text-red-500">*</span>
          </label>
          <select
            id="country"
            value={country}
            onChange={(e) => {
              setError('');
              onUpdate(e.target.value, city, workEligibility);
            }}
            className={`w-full px-3 md:px-4 py-2.5 md:py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors text-sm md:text-base ${error && !country.trim()
              ? 'border-red-500 focus:ring-red-200'
              : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
              }`}
          >
            <option value="">Select a country</option>
            {COUNTRIES.map((countryName) => (
              <option key={countryName} value={countryName}>
                {countryName}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="city" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
            City <span className="text-red-500">*</span>
          </label>
          <input
            id="city"
            type="text"
            value={city}
            onChange={(e) => {
              setError('');
              onUpdate(country, e.target.value, workEligibility);
            }}
            placeholder="e.g., San Francisco"
            className={`w-full px-3 md:px-4 py-2.5 md:py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors text-sm md:text-base ${error && !city.trim()
              ? 'border-red-500 focus:ring-red-200'
              : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
              }`}
          />
        </div>

        {/* Work Eligibility Field */}
        <div>
          <div className="flex items-start gap-2 md:gap-3">
            <input
              id="workEligibility"
              type="checkbox"
              checked={workEligibility === true}
              onChange={(e) => {
                setError('');
                onUpdate(country, city, e.target.checked);
              }}
              className="w-4 h-4 md:w-5 md:h-5 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer flex-shrink-0"
            />
            <label htmlFor="workEligibility" className="text-xs md:text-sm font-medium text-gray-700 cursor-pointer leading-tight">
              I am eligible to work where this role is located without sponsorship<span className="text-red-500">*</span>
            </label>
          </div>
        </div>

        {error && <p className="text-xs md:text-sm text-red-600 text-center">{error}</p>}

        <div className="flex gap-3 md:gap-4">
          <button
            onClick={onBack}
            className="flex-1 bg-white text-gray-700 py-2.5 md:py-3 px-4 md:px-6 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-sm md:text-base"
          >
            Back
          </button>
          <button
            onClick={handleNext}
            className="flex-1 bg-blue-600 text-white py-2.5 md:py-3 px-4 md:px-6 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-sm md:text-base"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

